import { useState, useCallback, useMemo, useEffect } from 'react';

import moment from 'moment';

import { useGetLeadsKanbanQuery, useGetLeadsKanbanStatusCountsQuery } from '../services/api/leads.api';
import { ExtendedLead } from '../services/types/lead';

/**
 * Custom hook for managing Kanban board pagination with temporal filters,
 * virtualization support, and skeleton loading states
 *
 * @param {number | null} businessUnitId - The business unit ID to filter leads by
 * @returns {Object} Object containing pagination state, handlers and query data
 */
const useKanbanPagination = (businessUnitId: number | null) => {
  const [startDate, setStartDate] = useState<moment.Moment | null>(moment().subtract(7, 'days').startOf('day'));
  const [endDate, setEndDate] = useState<moment.Moment | null>(moment().endOf('day'));

  const [statusPages, setStatusPages] = useState<Record<string, number>>({
    CREATED: 0,
    HANDLED_BY_AI: 0,
    ASSIGNED_TO_SALESMAN: 0,
    PENDING_TO_BE_HANDLED: 0,
    HANDLED_BY_SALESMAN: 0,
    CLOSED: 0,
  });

  const [loadingStates, setLoadingStates] = useState<
    Record<
      string,
      {
        isLoadingMore: boolean;
        isRefreshing: boolean;
      }
    >
  >({
    CREATED: { isLoadingMore: false, isRefreshing: false },
    HANDLED_BY_AI: { isLoadingMore: false, isRefreshing: false },
    ASSIGNED_TO_SALESMAN: { isLoadingMore: false, isRefreshing: false },
    PENDING_TO_BE_HANDLED: { isLoadingMore: false, isRefreshing: false },
    HANDLED_BY_SALESMAN: { isLoadingMore: false, isRefreshing: false },
    CLOSED: { isLoadingMore: false, isRefreshing: false },
  });

  const pageSize = 20;

  const {
    data: kanbanData,
    isLoading: isInitialLoading,
    isFetching,
  } = useGetLeadsKanbanQuery(
    {
      businessUnitId: businessUnitId || 0,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      pageSize,
      statusPages,
    },
    {
      skip: !businessUnitId,
    }
  );

  const { data: statusCountsData, isLoading: isStatusCountsLoading } = useGetLeadsKanbanStatusCountsQuery(
    {
      businessUnitId: businessUnitId || 0,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
    },
    {
      skip: !businessUnitId,
    }
  );

  const [accumulatedLeads, setAccumulatedLeads] = useState<Record<string, ExtendedLead[]>>({});

  const mergedLeadsByStatus = useMemo(() => {
    if (!kanbanData) return accumulatedLeads;

    const merged = { ...accumulatedLeads };

    Object.entries(kanbanData.leadsByStatus).forEach(([status, newLeads]) => {
      const currentPage = statusPages[status];

      if (currentPage === 0) {
        merged[status] = newLeads;
      } else {
        const existing = merged[status] || [];
        const existingIds = new Set(existing.map((lead) => lead.id));
        const uniqueNewLeads = newLeads.filter((lead) => !existingIds.has(lead.id));
        merged[status] = [...existing, ...uniqueNewLeads];
      }
    });

    return merged;
  }, [kanbanData, accumulatedLeads, statusPages]);

  /**
   * Loads more leads for a specific status column
   *
   * @param {string} status - The lead status to load more leads for
   */
  const loadMoreLeads = useCallback(
    (status: string) => {
      if (!kanbanData?.paginationInfo[status]?.hasMore || loadingStates[status].isLoadingMore) {
        return;
      }

      setLoadingStates((prev) => ({
        ...prev,
        [status]: { ...prev[status], isLoadingMore: true },
      }));

      const nextPage = (statusPages[status] || 0) + 1;
      setStatusPages((prev) => ({
        ...prev,
        [status]: nextPage,
      }));
    },
    [kanbanData, loadingStates, statusPages]
  );

  const refreshAllLeads = useCallback(() => {
    setStatusPages({
      CREATED: 0,
      HANDLED_BY_AI: 0,
      ASSIGNED_TO_SALESMAN: 0,
      PENDING_TO_BE_HANDLED: 0,
      HANDLED_BY_SALESMAN: 0,
      CLOSED: 0,
    });
    setAccumulatedLeads({});
    setLoadingStates((prev) =>
      Object.keys(prev).reduce(
        (acc, status) => ({
          ...acc,
          [status]: { isLoadingMore: false, isRefreshing: true },
        }),
        {}
      )
    );
  }, []);

  /**
   * Updates date range filters and refreshes data
   *
   * @param {moment.Moment | null} newStartDate - New start date
   * @param {moment.Moment | null} newEndDate - New end date
   */
  const updateDateRange = useCallback(
    (newStartDate: moment.Moment | null, newEndDate: moment.Moment | null) => {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      refreshAllLeads();
    },
    [refreshAllLeads]
  );

  useEffect(() => {
    if (!isFetching && kanbanData) {
      setLoadingStates((prev) =>
        Object.keys(prev).reduce(
          (acc, status) => ({
            ...acc,
            [status]: { isLoadingMore: false, isRefreshing: false },
          }),
          {}
        )
      );

      setAccumulatedLeads((prevAccumulated) => {
        const merged = { ...prevAccumulated };

        Object.entries(kanbanData.leadsByStatus).forEach(([status, newLeads]) => {
          const currentPage = statusPages[status];

          if (currentPage === 0) {
            merged[status] = newLeads as ExtendedLead[];
          } else {
            const existing = merged[status] || [];
            const existingIds = new Set(existing.map((lead) => lead.id));
            const uniqueNewLeads = (newLeads as ExtendedLead[]).filter((lead) => !existingIds.has(lead.id));
            merged[status] = [...existing, ...uniqueNewLeads];
          }
        });

        return merged;
      });
    }
  }, [isFetching, kanbanData, statusPages]);

  return {
    leadsByStatus: mergedLeadsByStatus,
    statusCounts: statusCountsData?.statusCounts || {},
    paginationInfo: kanbanData?.paginationInfo || {},
    totalLeads: statusCountsData?.totalLeads || 0,
    isInitialLoading: isInitialLoading || isStatusCountsLoading,
    isFetching,
    loadingStates,
    startDate,
    endDate,
    updateDateRange,
    pageSize,
    statusPages,
    loadMoreLeads,
    refreshAllLeads,
    getSkeletonCount: (status: string) => {
      const totalCount = kanbanData?.paginationInfo[status]?.totalCount || 0;
      const currentLeads = mergedLeadsByStatus[status]?.length || 0;
      return Math.min(Math.max(3, Math.ceil((totalCount - currentLeads) / 4)), 8);
    },
    hasMoreLeads: (status: string) => kanbanData?.paginationInfo[status]?.hasMore || false,
  };
};

export default useKanbanPagination;
